import { useState, useEffect, useRef } from "react";
import { Box, IconButton, Tooltip, keyframes } from "@mui/material";
import MicIcon from "@mui/icons-material/Mic";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import MicOffIcon from "@mui/icons-material/MicOff";

// Sound wave bar animations
const soundBar1 = keyframes`
  0%, 100% {
    height: 4px;
  }
  50% {
    height: 18px;
  }
`;

const soundBar2 = keyframes`
  0%, 100% {
    height: 8px;
  }
  50% {
    height: 14px;
  }
`;

const soundBar3 = keyframes`
  0%, 100% {
    height: 10px;
  }
  50% {
    height: 22px;
  }
`;

const soundBar4 = keyframes`
  0%, 100% {
    height: 6px;
  }
  50% {
    height: 16px;
  }
`;

const soundBar5 = keyframes`
  0%, 100% {
    height: 9px;
  }
  50% {
    height: 20px;
  }
`;

const soundBar6 = keyframes`
  0%, 100% {
    height: 5px;
  }
  50% {
    height: 15px;
  }
`;

const soundBar7 = keyframes`
  0%, 100% {
    height: 7px;
  }
  50% {
    height: 19px;
  }
`;

interface VoiceRecorderProps {
  onTranscriptChange: (transcript: string) => void;
  language?: string;
  disabled?: boolean;
  maxLength?: number;
  currentText?: string;
  onRecordingChange?: (isRecording: boolean) => void;
  onFinalizingChange?: (isFinalizing: boolean) => void;
}

const processingPulse = keyframes`
  0%, 100% {
    opacity: 0.4;
  }
  50% {
    opacity: 1;
  }
`;

const spinnerRotate = keyframes`
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
`;

// Component for finalizing overlay (shown after clicking tick)
export const FinalizingOverlay: React.FC = () => {
  return (
    <Box
      sx={{
        position: "absolute",
        left: 0,
        top: 0,
        right: 0,
        bottom: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        pointerEvents: "none",
        zIndex: 2,
        backgroundColor: (theme) => theme.palette.mode === "dark" ? "rgba(0, 0, 0, 0.7)" : "rgba(255, 255, 255, 0.9)",
      }}
    >
      <Box
        sx={{
          width: 20,
          height: 20,
          border: (theme) => `3px solid ${theme.palette.primary.main}40`,
          borderTop: (theme) => `3px solid ${theme.palette.primary.main}`,
          borderRadius: "50%",
          animation: `${spinnerRotate} 0.8s linear infinite`,
        }}
      />
    </Box>
  );
};

// Component for the wave overlay
export const WaveOverlay: React.FC = () => {
  return (
    <Box
      sx={{
        position: "absolute",
        left: 0,
        top: 0,
        right: 0,
        bottom: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        pointerEvents: "none",
        zIndex: 1,
        backgroundColor: (theme) => theme.palette.mode === "dark" ? "rgba(0, 0, 0, 0.5)" : "rgba(255, 255, 255, 0.85)",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "5px",
          height: "30px",
        }}
      >
        {/* Bar 1 */}
        <Box
          sx={{
            width: "2.5px",
            backgroundColor: (theme) => theme.palette.primary.main,
            borderRadius: "1.25px",
            animation: `${soundBar1} 0.6s ease-in-out infinite`,
          }}
        />
        {/* Bar 2 */}
        <Box
          sx={{
            width: "2.5px",
            backgroundColor: (theme) => theme.palette.primary.main,
            borderRadius: "1.25px",
            animation: `${soundBar2} 0.8s ease-in-out infinite 0.1s`,
          }}
        />
        {/* Bar 3 */}
        <Box
          sx={{
            width: "2.5px",
            backgroundColor: (theme) => theme.palette.primary.main,
            borderRadius: "1.25px",
            animation: `${soundBar3} 0.7s ease-in-out infinite 0.2s`,
          }}
        />
        {/* Bar 4 */}
        <Box
          sx={{
            width: "2.5px",
            backgroundColor: (theme) => theme.palette.primary.main,
            borderRadius: "1.25px",
            animation: `${soundBar4} 0.9s ease-in-out infinite 0.15s`,
          }}
        />
        {/* Bar 5 */}
        <Box
          sx={{
            width: "2.5px",
            backgroundColor: (theme) => theme.palette.primary.main,
            borderRadius: "1.25px",
            animation: `${soundBar5} 0.75s ease-in-out infinite 0.05s`,
          }}
        />
        {/* Bar 6 */}
        <Box
          sx={{
            width: "2.5px",
            backgroundColor: (theme) => theme.palette.primary.main,
            borderRadius: "1.25px",
            animation: `${soundBar6} 0.85s ease-in-out infinite 0.25s`,
          }}
        />
        {/* Bar 7 */}
        <Box
          sx={{
            width: "2.5px",
            backgroundColor: (theme) => theme.palette.primary.main,
            borderRadius: "1.25px",
            animation: `${soundBar7} 0.65s ease-in-out infinite 0.3s`,
          }}
        />
      </Box>
    </Box>
  );
};

export const VoiceRecorder: React.FC<VoiceRecorderProps> = ({
  onTranscriptChange,
  language = "en-US",
  disabled = false,
  maxLength,
  currentText = "",
  onRecordingChange,
  onFinalizingChange,
}) => {
  const [isRecording, setIsRecording] = useState(false);
  const [isFinalizing, setIsFinalizing] = useState(false);
  const [isSupported, setIsSupported] = useState(true);
  const recognitionRef = useRef<any>(null);
  const isRecordingRef = useRef(false);
  const currentTextRef = useRef(currentText);
  const maxLengthRef = useRef(maxLength);
  const onTranscriptChangeRef = useRef(onTranscriptChange);
  const textBeforeRecordingRef = useRef(currentText);
  const finalizingTimeoutRef = useRef<any>(null);
  const isStoppingRef = useRef(false);

  // Keep refs updated
  useEffect(() => {
    currentTextRef.current = currentText;
  }, [currentText]);

  useEffect(() => {
    maxLengthRef.current = maxLength;
  }, [maxLength]);

  useEffect(() => {
    isRecordingRef.current = isRecording;
    onRecordingChange?.(isRecording);
  }, [isRecording, onRecordingChange]);

  useEffect(() => {
    onFinalizingChange?.(isFinalizing);
  }, [isFinalizing, onFinalizingChange]);

  useEffect(() => {
    onTranscriptChangeRef.current = onTranscriptChange;
  }, [onTranscriptChange]);

  useEffect(() => {
    // Check if speech recognition is supported
    const SpeechRecognition =
      (window as any).SpeechRecognition ||
      (window as any).webkitSpeechRecognition;

    if (!SpeechRecognition) {
      setIsSupported(false);
      return;
    }

    // Initialize speech recognition only once
    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = language;

    recognition.onresult = (event: any) => {
      let final = "";

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          final += transcript + " ";
        }
      }

      // Only update with final results directly to the input
      if (final) {
        const newText = currentTextRef.current + final;
        if (!maxLengthRef.current || newText.length <= maxLengthRef.current) {
          // Update the ref immediately to ensure subsequent results build on this
          currentTextRef.current = newText;
          onTranscriptChangeRef.current(newText);
          // Only hide finalizing during active recording, not during stop/finalize phase
          if (!isStoppingRef.current) {
            setIsFinalizing(false);
            if (finalizingTimeoutRef.current) {
              clearTimeout(finalizingTimeoutRef.current);
            }
          }
        }
      }
    };

    recognition.onerror = (event: any) => {
      console.error("Speech recognition error:", event.error);
      setIsFinalizing(false);
      isStoppingRef.current = false;
      if (finalizingTimeoutRef.current) {
        clearTimeout(finalizingTimeoutRef.current);
      }
      if (event.error === "no-speech") {
        // User didn't speak, just continue
        return;
      }
      if (event.error === "aborted") {
        // Recognition was aborted, this is expected when stopping
        return;
      }
      setIsRecording(false);
    };

    recognition.onend = () => {
      if (isRecordingRef.current) {
        // Restart if still recording (for continuous mode)
        try {
          recognition.start();
        } catch (e) {
          console.error("Error restarting recognition:", e);
          setIsRecording(false);
          setIsFinalizing(false);
          isStoppingRef.current = false;
        }
      } else if (isStoppingRef.current) {
        // Recognition ended after user stopped - wait for final results then cleanup
        if (finalizingTimeoutRef.current) {
          clearTimeout(finalizingTimeoutRef.current);
        }
        finalizingTimeoutRef.current = setTimeout(() => {
          setIsFinalizing(false);
          isStoppingRef.current = false;
        }, 800);
      } else {
        // Normal end, set a safety timeout to hide finalizing
        finalizingTimeoutRef.current = setTimeout(() => {
          setIsFinalizing(false);
        }, 1500);
      }
    };

    recognitionRef.current = recognition;

    return () => {
      if (recognitionRef.current) {
        try {
          recognitionRef.current.abort();
        } catch (e) {
          console.error("Error aborting recognition:", e);
        }
      }
      if (finalizingTimeoutRef.current) {
        clearTimeout(finalizingTimeoutRef.current);
      }
    };
  }, [language]);

  const startRecording = () => {
    if (!recognitionRef.current || disabled) return;

    try {
      // Save the current text before starting recording
      currentTextRef.current = currentText;
      textBeforeRecordingRef.current = currentText;
      recognitionRef.current.start();
      setIsRecording(true);
      setIsFinalizing(false);
    } catch (error) {
      console.error("Error starting recognition:", error);
    }
  };

  const stopRecording = () => {
    if (!recognitionRef.current) return;

    try {
      // Update state BEFORE stopping to prevent restart in onend handler
      setIsRecording(false);
      isRecordingRef.current = false;
      isStoppingRef.current = true;

      // Show finalizing indicator when stopping
      setIsFinalizing(true);
      recognitionRef.current.stop();

      // Safety timeout - prevent infinite loader (10 seconds max)
      if (finalizingTimeoutRef.current) {
        clearTimeout(finalizingTimeoutRef.current);
      }
      finalizingTimeoutRef.current = setTimeout(() => {
        setIsFinalizing(false);
        isStoppingRef.current = false;
      }, 10000);
    } catch (error) {
      console.error("Error stopping recognition:", error);
      setIsFinalizing(false);
      isStoppingRef.current = false;
    }
  };

  const cancelRecording = () => {
    if (!recognitionRef.current) return;

    try {
      isStoppingRef.current = false;
      recognitionRef.current.stop();
      setIsRecording(false);
      setIsFinalizing(false);
      if (finalizingTimeoutRef.current) {
        clearTimeout(finalizingTimeoutRef.current);
      }
      // Revert to the text before recording started
      onTranscriptChangeRef.current(textBeforeRecordingRef.current);
    } catch (error) {
      console.error("Error canceling recognition:", error);
    }
  };

  if (!isSupported) {
    return (
      <Tooltip title="Speech recognition is not supported in this browser">
        <IconButton size="small" disabled>
          <MicOffIcon sx={{ fontSize: 20 }} />
        </IconButton>
      </Tooltip>
    );
  }

  return (
    <>
      {isRecording ? (
        <Box sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
          {/* Cancel button */}
          <Tooltip title="Cancel recording">
            <IconButton
              onClick={cancelRecording}
              size="small"
              sx={{
                width: "24px",
                height: "24px",
                backgroundColor: (theme) => theme.palette.grey[400],
                color: "white",
                padding: 0,
                minWidth: "24px",
                zIndex: 2,
                "&:hover": {
                  backgroundColor: (theme) => theme.palette.grey[500],
                },
              }}
            >
              <CloseIcon sx={{ fontSize: 16, fontWeight: "bold" }} />
            </IconButton>
          </Tooltip>
          {/* Save button */}
          <Tooltip title="Stop and save recording">
            <IconButton
              onClick={stopRecording}
              size="small"
              sx={{
                width: "24px",
                height: "24px",
                backgroundColor: (theme) => theme.palette.grey[600],
                color: "white",
                padding: 0,
                minWidth: "24px",
                zIndex: 2,
                "&:hover": {
                  backgroundColor: (theme) => theme.palette.grey[700],
                },
              }}
            >
              <CheckIcon sx={{ fontSize: 16, fontWeight: "bold" }} />
            </IconButton>
          </Tooltip>
        </Box>
      ) : (
        <Tooltip title="Start voice recording">
          <IconButton
            onClick={startRecording}
            disabled={disabled}
            size="small"
            sx={{
              color: (theme) => theme.palette.text.secondary,
              "&:hover": {
                backgroundColor: (theme) =>
                  theme.palette.mode === "dark"
                    ? "rgba(255, 255, 255, 0.08)"
                    : "rgba(0, 0, 0, 0.04)",
              },
            }}
          >
            <MicIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Tooltip>
      )}
    </>
  );
};

// Hook to use voice recorder with wave overlay
export const useVoiceRecorder = (props: VoiceRecorderProps) => {
  const [isRecording, setIsRecording] = useState(false);
  const [isFinalizing, setIsFinalizing] = useState(false);
  const [isSupported, setIsSupported] = useState(true);
  const recognitionRef = useRef<any>(null);
  const isRecordingRef = useRef(false);
  const currentTextRef = useRef(props.currentText || "");
  const maxLengthRef = useRef(props.maxLength);
  const onTranscriptChangeRef = useRef(props.onTranscriptChange);
  const textBeforeRecordingRef = useRef(props.currentText || "");
  const finalizingTimeoutRef = useRef<any>(null);
  const isStoppingRef = useRef(false);

  // Keep refs updated
  useEffect(() => {
    currentTextRef.current = props.currentText || "";
  }, [props.currentText]);

  useEffect(() => {
    maxLengthRef.current = props.maxLength;
  }, [props.maxLength]);

  useEffect(() => {
    isRecordingRef.current = isRecording;
  }, [isRecording]);

  useEffect(() => {
    onTranscriptChangeRef.current = props.onTranscriptChange;
  }, [props.onTranscriptChange]);

  useEffect(() => {
    // Check if speech recognition is supported
    const SpeechRecognition =
      (window as any).SpeechRecognition ||
      (window as any).webkitSpeechRecognition;

    if (!SpeechRecognition) {
      setIsSupported(false);
      return;
    }

    // Initialize speech recognition only once
    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = props.language || "en-US";

    recognition.onresult = (event: any) => {
      let final = "";

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          final += transcript + " ";
        }
      }

      // Only update with final results directly to the input
      if (final) {
        const newText = currentTextRef.current + final;
        if (!maxLengthRef.current || newText.length <= maxLengthRef.current) {
          // Update the ref immediately to ensure subsequent results build on this
          currentTextRef.current = newText;
          onTranscriptChangeRef.current(newText);
          // Only hide finalizing during active recording, not during stop/finalize phase
          if (!isStoppingRef.current) {
            setIsFinalizing(false);
            if (finalizingTimeoutRef.current) {
              clearTimeout(finalizingTimeoutRef.current);
            }
          }
        }
      }
    };

    recognition.onerror = (event: any) => {
      console.error("Speech recognition error:", event.error);
      setIsFinalizing(false);
      isStoppingRef.current = false;
      if (finalizingTimeoutRef.current) {
        clearTimeout(finalizingTimeoutRef.current);
      }
      if (event.error === "no-speech") {
        // User didn't speak, just continue
        return;
      }
      if (event.error === "aborted") {
        // Recognition was aborted, this is expected when stopping
        return;
      }
      setIsRecording(false);
    };

    recognition.onend = () => {
      if (isRecordingRef.current) {
        // Restart if still recording (for continuous mode)
        try {
          recognition.start();
        } catch (e) {
          console.error("Error restarting recognition:", e);
          setIsRecording(false);
          setIsFinalizing(false);
          isStoppingRef.current = false;
        }
      } else if (isStoppingRef.current) {
        // Recognition ended after user stopped - wait for final results then cleanup
        if (finalizingTimeoutRef.current) {
          clearTimeout(finalizingTimeoutRef.current);
        }
        finalizingTimeoutRef.current = setTimeout(() => {
          setIsFinalizing(false);
          isStoppingRef.current = false;
        }, 800);
      } else {
        // Normal end, set a safety timeout to hide finalizing
        finalizingTimeoutRef.current = setTimeout(() => {
          setIsFinalizing(false);
        }, 1500);
      }
    };

    recognitionRef.current = recognition;

    return () => {
      if (recognitionRef.current) {
        try {
          recognitionRef.current.abort();
        } catch (e) {
          console.error("Error aborting recognition:", e);
        }
      }
      if (finalizingTimeoutRef.current) {
        clearTimeout(finalizingTimeoutRef.current);
      }
    };
  }, [props.language]);

  const startRecording = () => {
    if (!recognitionRef.current || props.disabled) return;

    try {
      // Save the current text before starting recording
      currentTextRef.current = props.currentText || "";
      textBeforeRecordingRef.current = props.currentText || "";
      recognitionRef.current.start();
      setIsRecording(true);
      setIsFinalizing(false);
    } catch (error) {
      console.error("Error starting recognition:", error);
    }
  };

  const stopRecording = () => {
    if (!recognitionRef.current) return;

    try {
      // Update state BEFORE stopping to prevent restart in onend handler
      setIsRecording(false);
      isRecordingRef.current = false;
      isStoppingRef.current = true;

      // Show finalizing indicator when stopping
      setIsFinalizing(true);
      recognitionRef.current.stop();

      // Safety timeout - prevent infinite loader (10 seconds max)
      if (finalizingTimeoutRef.current) {
        clearTimeout(finalizingTimeoutRef.current);
      }
      finalizingTimeoutRef.current = setTimeout(() => {
        setIsFinalizing(false);
        isStoppingRef.current = false;
      }, 10000);
    } catch (error) {
      console.error("Error stopping recognition:", error);
      setIsFinalizing(false);
      isStoppingRef.current = false;
    }
  };

  const cancelRecording = () => {
    if (!recognitionRef.current) return;

    try {
      isStoppingRef.current = false;
      recognitionRef.current.stop();
      setIsRecording(false);
      setIsFinalizing(false);
      if (finalizingTimeoutRef.current) {
        clearTimeout(finalizingTimeoutRef.current);
      }
      // Revert to the text before recording started
      onTranscriptChangeRef.current(textBeforeRecordingRef.current);
    } catch (error) {
      console.error("Error canceling recognition:", error);
    }
  };

  return {
    isRecording,
    isFinalizing,
    isSupported,
    startRecording,
    stopRecording,
    cancelRecording,
  };
};

export default VoiceRecorder;
